import React, { useMemo, useState, useEffect } from 'react';
import {
  Button,
  Form,
  Alert,
  ProgressBar,
} from 'react-bootstrap';
import ReactQuill from 'react-quill';
import { FaEye, FaPen } from 'react-icons/fa';
import 'react-quill/dist/quill.snow.css';
import './NoticeCreate.css';
import ContentUnitTag from '../common/ContentUnitTag';
import { useUser } from '../../context/UserContext';
import { useTenant } from '../../context/TenantContext';
import { useMembership } from '../../context/MembershipContext';
import { isTenantAdminForTenant } from '../../platform/membershipPermissions';
import {
  createNotice,
  fetchMaxNoticeOrder,
  updateNotice,
  uploadNoticeFiles,
  uploadNoticeThumbnail,
  countActivePinnedNotices,
} from '../../services/noticesService';
import { useToast } from '../../platform/toast/ToastProvider';
import FeaturedCarouselPinWarning, {
  useFeaturedCarouselPinWarning,
} from '../../platform/FeaturedCarouselPinWarning';
import { fetchAllUnits } from '../../services/unitsService';
import { normalizeAttachments, syncAttachmentFields } from '../../platform/contentAttachments';
import ContentAttachmentsEditor from '../../platform/ContentAttachmentsEditor';
import { db } from '../../firebase/firebase-config';
import {
  getAllUserEmailsForTenant,
  getUserEmailsByUnitForTenant,
  sendMassEmail,
} from '../../utils/emailUtils';
import {
  massEmailGreeting,
  massEmailPortalUrl,
  massEmailSignoff,
} from '../../platform/massEmailCopy';
import {
  buildPinFields,
  defaultPinnedUntil,
  isPinnedActive,
  toDateInputValue,
} from '../../platform/pinnedContent';
import { useRichTextToolbarTitles } from '../../platform/richTextEditor';

const EDITOR_COLORS = [
  '#212121',
  '#424242',
  '#757575',
  '#d32f2f',
  '#b71c1c',
  '#1565c0',
  '#2e7d32',
  '#e65100',
];

const EDITOR_BACKGROUNDS = [
  '#ffffff',
  '#fafbfc',
  '#fff3e0',
  '#e8f5e9',
  '#e3f2fd',
  '#fce4ec',
  '#f3e5f5',
  '#fffde7',
];

const EDITOR_MODULES = {
  toolbar: [
    [{ header: [1, 2, 3, false] }],
    ['bold', 'italic', 'underline', 'strike'],
    [{ color: EDITOR_COLORS }, { background: EDITOR_BACKGROUNDS }],
    [{ list: 'ordered' }, { list: 'bullet' }, { indent: '-1' }, { indent: '+1' }],
    ['blockquote', { align: [] }],
    ['link'],
    ['clean'],
  ],
};

const EDITOR_FORMATS = [
  'header',
  'bold',
  'italic',
  'underline',
  'strike',
  'color',
  'background',
  'list',
  'bullet',
  'indent',
  'blockquote',
  'align',
  'link',
];

function isQuillEmpty(html) {
  if (!html) return true;
  const stripped = html
    .replace(/<[^>]*>/g, ' ')
    .replace(/&nbsp;/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  return stripped.length === 0;
}

function NoticeCreate({ onNoticeCreated, noticeToEdit }) {
  const [title, setTitle] = useState('');
  const [text, setText] = useState('');
  const [editorMode, setEditorMode] = useState('write');
  const [unit, setUnit] = useState('general');
  const [unitName, setUnitName] = useState('Splošno');
  const [units, setUnits] = useState([]);
  const [existingAttachments, setExistingAttachments] = useState([]);
  const [newFiles, setNewFiles] = useState([]);
  const [thumbnail, setThumbnail] = useState(null);
  const [error, setError] = useState('');
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  const [sendEmailAll, setSendEmailAll] = useState(false);
  const [sendEmailUnit, setSendEmailUnit] = useState(false);

  const [isPinned, setIsPinned] = useState(false);
  const [pinnedUntilDate, setPinnedUntilDate] = useState(toDateInputValue(defaultPinnedUntil()));

  const [maxOrder, setMaxOrder] = useState(0);

  const { user } = useUser();
  const { tenantId, config } = useTenant();
  const { memberships } = useMembership();
  const toast = useToast();
  const isTenantAdmin = isTenantAdminForTenant(memberships, tenantId, user);

  const modules = useMemo(() => EDITOR_MODULES, []);
  const quillContainerRef = useRichTextToolbarTitles(editorMode === 'write');
  const { showOverflowWarning } = useFeaturedCarouselPinWarning({
    tenantId,
    isPinned,
    excludeId: noticeToEdit?.id ?? null,
    countActivePinned: countActivePinnedNotices,
  });

  useEffect(() => {
    if (noticeToEdit) {
      setTitle(noticeToEdit.title);
      setText(noticeToEdit.text);
      setUnit(noticeToEdit.unit || 'general');
      setUnitName(noticeToEdit.unitName || 'Splošno');
      setExistingAttachments(normalizeAttachments(noticeToEdit));
      setNewFiles([]);
      const noticePinned = isPinnedActive(noticeToEdit);
      setIsPinned(noticePinned);
      setPinnedUntilDate(
        noticePinned
          ? toDateInputValue(noticeToEdit.pinnedUntil)
          : toDateInputValue(defaultPinnedUntil()),
      );
    }
  }, [noticeToEdit]);

  useEffect(() => {
    if (!tenantId) return;
    fetchAllUnits(tenantId).then(setUnits);
  }, [tenantId]);

  useEffect(() => {
    if (!tenantId) return;
    fetchMaxNoticeOrder(tenantId)
      .then(setMaxOrder)
      .catch((fetchError) => {
        console.error('Failed to fetch max order:', fetchError);
      });
  }, [tenantId]);

  const handleUnitChange = (e) => {
    const selectedUnitId = e.target.value;
    const selectedUnit = units.find((u) => u.id === selectedUnitId);
    setUnit(selectedUnitId);
    setUnitName(selectedUnit ? selectedUnit.unitName : 'Splošno');
  };

  const handleThumbnailChange = (e) => {
    const selectedImage = e.target.files[0];
    if (selectedImage && !selectedImage.type.startsWith('image/')) {
      setError('Za ikono so dovoljene samo slikovne datoteke.');
      setThumbnail(null);
    } else {
      setError('');
      setThumbnail(selectedImage);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!user) {
      setError('Za ustvarjanje obvestila morate biti prijavljeni.');
      return;
    }
    if (!title.trim() || isQuillEmpty(text)) {
      setError('Naslov in besedilo ne smeta biti prazna.');
      return;
    }

    setUploading(true);
    setError('');

    let uploadedAttachments = [];
    if (newFiles.length) {
      try {
        uploadedAttachments = await uploadNoticeFiles(newFiles, setUploadProgress, tenantId);
      } catch (uploadError) {
        console.error('Upload error:', uploadError);
        setError('Nalaganje datotek ni uspelo. Poskusite znova.');
        setUploading(false);
        return;
      }
    }

    let thumbnailUrl = noticeToEdit?.thumbnailUrl || '';
    if (thumbnail) {
      try {
        thumbnailUrl = await uploadNoticeThumbnail(thumbnail, setUploadProgress, tenantId);
      } catch (uploadError) {
        console.error('Thumbnail upload error:', uploadError);
        setError('Nalaganje ikone ni uspelo. Poskusite znova.');
        setUploading(false);
        return;
      }
    }

    await saveNotice([...existingAttachments, ...uploadedAttachments], thumbnailUrl);
  };

  const saveNotice = async (attachments, thumbnailUrl) => {
    const attachmentFields = syncAttachmentFields(attachments);
    let pinFields;

    try {
      pinFields = buildPinFields({ isPinned, pinnedUntilDate });
    } catch (pinError) {
      setError(pinError.message);
      setUploading(false);
      return;
    }

    const newNotice = {
      title,
      text,
      unit,
      unitName,
      ...attachmentFields,
      thumbnailUrl: thumbnailUrl || '',
      pinnedUntil: pinFields.pinnedUntil,
      pinPriority: pinFields.pinPriority,
    };

    if (!noticeToEdit) {
      newNotice.uid = user.uid;
      newNotice.displayName = user.displayName || 'Anonimen';
      newNotice.order = maxOrder + 1;
    }

    try {
      if (noticeToEdit) {
        await updateNotice(noticeToEdit.id, newNotice);
        toast.success('Obvestilo je bilo posodobljeno.');
      } else {
        await createNotice(user, newNotice, tenantId);
        toast.success('Obvestilo je bilo objavljeno.');

        const subject = `Novo obvestilo: ${title}`;
        const html = `
        <h2>${massEmailGreeting(config)}</h2>
        <p>
          Na ${massEmailPortalUrl('/notices')} je bilo objavljeno novo obvestilo z naslovom
          <strong>${title}</strong>.
        </p>
        <p>
          Vzemite si nekaj časa in se seznanite z vsebino obvestila ter aktivnostmi sindikata.
        </p>
        <p>Skupaj smo močnejši!</p>
        <p>Lep pozdrav,<br/>${massEmailSignoff(config)}</p>
      `;

        try {
          const recipientsSet = new Set();

          if (sendEmailAll) {
            const allUserEmails = await getAllUserEmailsForTenant(db, tenantId);
            allUserEmails.forEach((email) => recipientsSet.add(email));
          }

          if (sendEmailUnit && unit) {
            const unitEmails = await getUserEmailsByUnitForTenant(db, tenantId, unit);
            unitEmails.forEach((email) => recipientsSet.add(email));
          }

          if (recipientsSet.size > 0) {
            await sendMassEmail(subject, html, Array.from(recipientsSet));
          }
        } catch (emailError) {
          console.error('Error sending email notifications:', emailError);
        }
      }

      setTitle('');
      setText('');
      setEditorMode('write');
      setUnit('general');
      setUnitName('Splošno');
      setNewFiles([]);
      setExistingAttachments([]);
      setThumbnail(null);
      setError('');
      setUploading(false);

      setSendEmailAll(false);
      setSendEmailUnit(false);
      setIsPinned(false);
      setPinnedUntilDate(toDateInputValue(defaultPinnedUntil()));

      onNoticeCreated();
    } catch (saveError) {
      setError(saveError.message || 'Ustvarjanje ali posodabljanje obvestila ni uspelo. Poskusite znova.');
      toast.error('Ustvarjanje ali posodabljanje obvestila ni uspelo. Poskusite znova.');
      console.error('Error creating/updating notice:', saveError);
      setUploading(false);
    }
  };

  return (
    <div className="notice-create">
      {error && <Alert variant="danger">{error}</Alert>}

      <Form onSubmit={handleSubmit}>
        <div className="notice-create-card platform-content-detail-card">
          <div className="notice-create-card__header">
            <h2 className="notice-create-card__title">Vsebina obvestila</h2>
            <p className="notice-create-card__subtitle">
              Napišite jasno in pregledno — člani bodo videli enako oblikovanje kot v predogledu.
            </p>
          </div>

          <div className="notice-create-card__body">
            <Form.Group className="notice-create-field">
              <Form.Label>Naslov</Form.Label>
              <Form.Control
                type="text"
                placeholder="Kratek, jasen naslov obvestila"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                className="notice-create-title-input"
              />
            </Form.Group>

            <div className="notice-create-editor-block">
              <div className="notice-create-editor-block__head">
                <Form.Label className="mb-0">Besedilo</Form.Label>
                <div className="notice-create-mode-tabs" role="tablist" aria-label="Način urejanja">
                  <button
                    type="button"
                    role="tab"
                    aria-selected={editorMode === 'write'}
                    className={`notice-create-mode-tab${editorMode === 'write' ? ' notice-create-mode-tab--active' : ''}`}
                    onClick={() => setEditorMode('write')}
                  >
                    <FaPen aria-hidden="true" />
                    Piši
                  </button>
                  <button
                    type="button"
                    role="tab"
                    aria-selected={editorMode === 'preview'}
                    className={`notice-create-mode-tab${editorMode === 'preview' ? ' notice-create-mode-tab--active' : ''}`}
                    onClick={() => setEditorMode('preview')}
                  >
                    <FaEye aria-hidden="true" />
                    Predogled
                  </button>
                </div>
              </div>

              {editorMode === 'write' ? (
                <div ref={quillContainerRef} className="notice-create-quill">
                  <ReactQuill
                    value={text}
                    onChange={setText}
                    placeholder="Začnite pisati obvestilo…"
                    theme="snow"
                    modules={modules}
                    formats={EDITOR_FORMATS}
                  />
                </div>
              ) : (
                <div className="notice-create-preview">
                  <div className="notice-create-preview__meta">
                    <ContentUnitTag item={{ unit, unitName }} />
                    {title.trim() && (
                      <>
                        <span className="notice-create-preview__separator">·</span>
                        <span className="notice-create-preview__title-inline">{title}</span>
                      </>
                    )}
                  </div>
                  {isQuillEmpty(text) ? (
                    <p className="notice-create-preview__empty">
                      Predogled se prikaže, ko vnesete besedilo.
                    </p>
                  ) : (
                    <div
                      className="platform-content-detail__content notice-create-preview__content"
                      dangerouslySetInnerHTML={{ __html: text }}
                    />
                  )}
                </div>
              )}

              <p className="notice-create-hint">
                Nasvet: uporabite <strong>naslove</strong>, <strong>sezname</strong> in{' '}
                <strong>barve</strong> za poudarke. Pred objavo preverite zavihek Predogled.
              </p>
            </div>
          </div>
        </div>

        <div className="notice-create-card platform-content-detail-card">
          <div className="notice-create-card__header">
            <h2 className="notice-create-card__title">Nastavitve objave</h2>
          </div>

          <div className="notice-create-card__body notice-create-settings">
            <Form.Group className="notice-create-field">
              <Form.Label>Enota</Form.Label>
              <Form.Select value={unit} onChange={handleUnitChange}>
                <option value="general">Splošno</option>
                {units
                  .filter((u) => isTenantAdmin || u.id === user.unit)
                  .map((u) => (
                    <option key={u.id} value={u.id}>
                      {u.unitName}
                    </option>
                  ))}
              </Form.Select>
            </Form.Group>

            <Form.Group className="notice-create-field">
              <Form.Check
                type="checkbox"
                id="notice-pin-toggle"
                label="Pripni na vrh seznama"
                checked={isPinned}
                onChange={(event) => setIsPinned(event.target.checked)}
              />
            </Form.Group>

            {isPinned && (
              <div className="notice-create-pin-fields">
                <FeaturedCarouselPinWarning
                  visible={showOverflowWarning}
                  module="notices"
                />
                <Form.Group className="notice-create-field">
                  <Form.Label>Pripeto do</Form.Label>
                  <Form.Control
                    type="date"
                    value={pinnedUntilDate}
                    onChange={(event) => setPinnedUntilDate(event.target.value)}
                    required
                  />
                </Form.Group>
              </div>
            )}

            <div className="notice-create-uploads">
              <ContentAttachmentsEditor
                label="PDF priloge (neobvezno)"
                accept="application/pdf,.pdf"
                pdfOnly
                existingAttachments={existingAttachments}
                onExistingAttachmentsChange={setExistingAttachments}
                newFiles={newFiles}
                onNewFilesChange={setNewFiles}
                hint="Dodate lahko več PDF datotek k obvestilu."
              />

              <Form.Group className="notice-create-field">
                <Form.Label>Ikona v seznamu</Form.Label>
                <Form.Text className="text-muted notice-create-field__hint">
                  Neobvezno — prikaže se poleg naslova v seznamu obvestil.
                </Form.Text>
                <Form.Control
                  type="file"
                  accept="image/*"
                  onChange={handleThumbnailChange}
                />
              </Form.Group>
            </div>

            {!noticeToEdit && (
            <div className="notice-create-email-toggles">
              <Form.Check
                type="switch"
                id="sendEmailAllSwitch"
                label="Pošlji e-pošto vsem uporabnikom"
                checked={sendEmailAll}
                onChange={(e) => setSendEmailAll(e.target.checked)}
              />
              <Form.Check
                type="switch"
                id="sendEmailUnitSwitch"
                label="Pošlji e-pošto samo uporabnikom iz te enote"
                checked={sendEmailUnit}
                onChange={(e) => setSendEmailUnit(e.target.checked)}
              />
            </div>
            )}
          </div>
        </div>

        {uploading && (
          <ProgressBar
            now={uploadProgress}
            label={`${Math.round(uploadProgress)}%`}
            striped
            className="notice-create-progress"
          />
        )}

        <div className="notice-create-actions">
          <Button variant="primary" type="submit" disabled={uploading}>
            {noticeToEdit ? 'Posodobi obvestilo' : 'Objavi obvestilo'}
          </Button>
        </div>
      </Form>
    </div>
  );
}

export default NoticeCreate;
